import i18nFactory from '@yandex-int/i18n';
import { Epic } from 'redux-observable';
import { EMPTY, Observable } from 'rxjs';

import { Action } from '../../../actions';
import { clearFreelancerFeedbackForm } from '../../../actions/freelancers/feedback-form';
import {
    ADD_FREELANCER_FEEDBACK_START,
    AddFreelancerFeedbackStartAction,
    addFreelancerFeedbackSuccess,
    DELETE_FREELANCER_FEEDBACK_START,
    DeleteFreelancerFeedbackStartAction,
    deleteFreelancerFeedbackSuccess,
    UPDATE_FREELANCER_FEEDBACK_START,
    UpdateFreelancerFeedbackStartAction,
    updateFreelancerFeedbackSuccess
} from '../../../actions/freelancers/freelancer-feedback';
import { mapFreelancerFeedbackItem } from '../../../mappers/freelancers';
import { freelancerFeedbackErrorHandler } from '../../../service/FreelancerFeedbackErrorHandler/FreelancerFeedbackErrorHandler';
import { showNoticeMessage } from '../../../service/GridHelpers/GridHelpers';
import { EpicDeps } from '../../../service/StoreFactory';
import { NoticeType } from '../../../typings/grid';
import { State } from '../../../typings/state';

import * as keyset from './freelancer-feedback.i18n';

const i18n = i18nFactory(keyset);

export const addFreelancerFeedbackEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(ADD_FREELANCER_FEEDBACK_START).mergeMap(
        (action: AddFreelancerFeedbackStartAction): Observable<Action> => {
            return Observable.fromPromise(
                deps.freelancerMutationExecutor.addFeedbackForFreelancer({
                    willRecommend: action.payload.willRecommend,
                    freelancerId: action.payload.freelancerId,
                    feedbackText: action.payload.feedbackText,
                    overallMark: String(action.payload.freelancerScore)
                })
            )
                .map(response => {
                    const { feedback } = response.addFeedbackForFreelancer;
                    return {
                        freelancerId: String(feedback.freelancerId),
                        item: mapFreelancerFeedbackItem(feedback)
                    };
                })
                .mergeMap(({ freelancerId, item }) => {
                    showNoticeMessage(NoticeType.success, i18n('success-message'));
                    return Observable.concat(
                        Observable.of(addFreelancerFeedbackSuccess(freelancerId, item)),
                        Observable.of(clearFreelancerFeedbackForm())
                    );
                })
                .catch(error => {
                    freelancerFeedbackErrorHandler(error.errors);

                    return Observable.of(clearFreelancerFeedbackForm());
                });
        }
    );
};

export const updateFreelancerFeedbackEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(UPDATE_FREELANCER_FEEDBACK_START).mergeMap(
        (action: UpdateFreelancerFeedbackStartAction): Observable<Action> => {
            return Observable.fromPromise(
                deps.freelancerMutationExecutor.updateFreelancerFeedback({
                    feedbackId: action.payload.feedbackId,
                    overallMark: String(action.payload.freelancerScore),
                    willRecommend: action.payload.willRecommend,
                    freelancerId: action.payload.freelancerId,
                    feedbackText: action.payload.feedbackText
                })
            )
                .map(response => response.updateFreelancerFeedback.feedback)
                .mergeMap(
                    (feedback): Observable<Action> =>
                        Observable.concat(
                            Observable.of(updateFreelancerFeedbackSuccess(mapFreelancerFeedbackItem(feedback))),
                            Observable.of(clearFreelancerFeedbackForm())
                        )
                )
                .catch(() => {
                    showNoticeMessage(NoticeType.error, i18n('error-message'));
                    return Observable.of(clearFreelancerFeedbackForm());
                });
        }
    );
};

export const deleteFreelancerFeedbackEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(DELETE_FREELANCER_FEEDBACK_START).mergeMap(
        (action: DeleteFreelancerFeedbackStartAction): Observable<Action> => {
            return Observable.fromPromise(
                deps.freelancerMutationExecutor.deleteFreelancerFeedback({
                    freelancerId: action.payload.freelancerId,
                    feedbackId: action.payload.feedbackItemId
                })
            )
                .map(response => response.deleteFreelancerFeedback)
                .map(data => {
                    if (data.validationResult) {
                        throw new Error();
                    } else {
                        return deleteFreelancerFeedbackSuccess(action.payload);
                    }
                })
                .catch(() => {
                    showNoticeMessage(NoticeType.error, i18n('failed-to-delete-comment-message'));
                    return EMPTY;
                });
        }
    );
};
